import { createContext, useContext, useState, useEffect, ReactNode, useRef, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';

interface SidebarContextType {
  isOpen: boolean;
  toggleSidebar: () => void;
  setIsOpen: (isOpen: boolean) => void;
  registerSidebarElement: (el: HTMLElement | null) => void;
  registerTriggerElement: (el: HTMLElement | null) => void;
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

export const useSidebar = () => {
  const context = useContext(SidebarContext);
  if (context === undefined) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
};

interface SidebarProviderProps {
  children: ReactNode;
}

export const SidebarProvider = ({ children }: SidebarProviderProps) => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // Refs to identify sidebar panel and trigger elements
  const sidebarRef = useRef<HTMLElement | null>(null);
  const triggerRef = useRef<HTMLElement | null>(null);

  const registerSidebarElement = useCallback((el: HTMLElement | null) => {
    sidebarRef.current = el;
  }, []);

  const registerTriggerElement = useCallback((el: HTMLElement | null) => {
    triggerRef.current = el;
  }, []);

  // Initialize from localStorage, defaulting to open for logged-in users on large screens
  useEffect(() => {
    const stored = localStorage.getItem('sidebar-open');
    const isMobile = window.innerWidth < 768;
    
    if (isMobile) {
      setIsOpen(false);
    } else if (stored !== null) {
      // User has a stored preference, respect it
      setIsOpen(JSON.parse(stored));
    } else {
      // No stored preference: default to open for logged-in users on desktop
      setIsOpen(!!user);
    }
    setIsInitialized(true);
  }, [user]);

  // Persist to localStorage on desktop only
  useEffect(() => {
    if (!isInitialized) return;
    const isMobile = window.innerWidth < 768;
    if (!isMobile) {
      localStorage.setItem('sidebar-open', JSON.stringify(isOpen));
    } else {
      // Ensure we don't persist an open state for mobile sessions
      localStorage.setItem('sidebar-open', 'false');
    }
  }, [isOpen, isInitialized]);

  // Close behavior on any interaction while open on mobile
  // IMPORTANT: use `click` (not `pointerdown`) to avoid cancelling the user's click
  // on underlying UI when the sidebar closes and layout shifts.
  useEffect(() => {
    if (!isOpen) return;
    const isMobile = window.innerWidth < 768;
    if (!isMobile) return;

    const onDocumentClick = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (!target) return;

      // If tapping the hamburger trigger, do nothing here (toggle handles it)
      if (triggerRef.current && triggerRef.current.contains(target as Node)) {
        return;
      }

      const insideSidebar = sidebarRef.current && sidebarRef.current.contains(target as Node);

      if (insideSidebar) {
        // If the user clicked a navigation element, close after navigation starts
        const clickedElement = target as HTMLElement;
        const isNavigationElement =
          clickedElement.tagName === 'A' ||
          !!clickedElement.closest('a') ||
          !!clickedElement.closest('button');

        if (isNavigationElement) {
          setTimeout(() => setIsOpen(false), 150);
        }

        return;
      }

      // Click outside sidebar: close AFTER the click has been processed
      setTimeout(() => setIsOpen(false), 0);
    };

    document.addEventListener('click', onDocumentClick, true);
    return () => document.removeEventListener('click', onDocumentClick, true);
  }, [isOpen]);

  const toggleSidebar = () => setIsOpen((prev) => !prev);

  return (
    <SidebarContext.Provider value={{
      isOpen: isInitialized ? isOpen : false,
      toggleSidebar,
      setIsOpen,
      registerSidebarElement,
      registerTriggerElement,
    }}>
      {children}
    </SidebarContext.Provider>
  );
};